import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ZodError } from 'zod';
import { css, Theme, useTheme } from '@emotion/react';
import { Code, CreditCard, Detective, DownloadSimple, Flask, Plus } from '@phosphor-icons/react';
import type { AccountAccountingSummary } from 'api/account/methods/loadAccountAccountingSummary';
import { AccessRights } from 'common/account/AccessRights';
import type { AccountDocument } from 'common/account/AccountDocument';
import { AccountPlan, AccountPlanInfos, AccountPlanTexts, AccountUserRole } from 'common/CommonConstants';
import { EditAppContext, useEditApp } from 'client/lib/EditApp';
import { useUser } from 'client/lib/User';
import { AccountState, useAccountState } from 'client/state/AccountState';
import { AccountActions } from 'client/pages/profile/components/AccountActions';
import { PaidPlanBreakdown } from 'client/pages/profile/components/PaidPlanBreakdown';
import { ProfilePicture } from 'client/pages/profile/components/ProfilePicture';
import { Banner } from 'client/components/Banner';
import { Button } from 'client/components/Button';
import { Catalog, CatalogItemProps } from 'client/components/Catalog';
import { EditAppSaveButton } from 'client/components/EditApp/EditAppSaveButton';
import { Input } from 'client/components/Input';
import { Section } from 'client/components/Section/Section';
import { Stack } from 'client/components/Stack';
import { Tag } from 'client/components/Tag';
import { Typography } from 'client/components/Typography';
import { AnonymousTrialOnboarding } from './components/AnonymousTrialOnboarding';
import { UpgradeToPaidDialog } from './components/UpgradeToPaidDialog';
import { UserList } from './components/UserList';

export const AccountTab = () => {
  const theme = useTheme();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const { user } = useUser();
  const isAnonymousTrial = user && !!user.anonymousTrial;

  const editAppContext = useEditApp<AccountDocument>({
    idKey: '*',
    loadUrl: '/account/load',
    saveUrl: '/account/save',
    store: useAccountState,
    validator: (data: any) => new ZodError<any>([]),
  });

  const planCatalogItems = useMemo<Record<AccountPlan, CatalogItemProps>>(
    () => ({
      [AccountPlan.DEVELOPER]: {
        id: 'dev',
        title: AccountPlanTexts[AccountPlan.DEVELOPER],
        subtitle: AccountPlanInfos[AccountPlan.DEVELOPER],
        icon: <Code color={theme.color.grey[100]} />,
      },
      [AccountPlan.FREE]: {
        id: 'free',
        title: AccountPlanTexts[AccountPlan.FREE],
        subtitle: AccountPlanInfos[AccountPlan.FREE],
        icon: <Flask color={theme.color.grey[100]} />,
        action: (
          <Button icon={<Plus weight="bold" />} small primary onClick={() => setUpgradeOpen(true)}>
            Upgrade
          </Button>
        ),
      },
      [AccountPlan.ANONYMOUS]: {
        id: 'anonymous',
        title: AccountPlanTexts[AccountPlan.ANONYMOUS],
        subtitle: AccountPlanInfos[AccountPlan.ANONYMOUS],
        icon: <Detective color={theme.color.grey[100]} />,
      },
      [AccountPlan.PAID]: {
        id: 'paid',
        title: AccountPlanTexts[AccountPlan.PAID],
        subtitle: AccountPlanInfos[AccountPlan.PAID],
        icon: <CreditCard />,
      },
    }),
    [theme]
  );

  const document = editAppContext.store((state) => state.document);
  const isAdmin = document?.accountUserRoleID === AccountUserRole.ADMINISTRATOR;
  const { hasAccessRight } = useUser();
  const isUserAdmin = hasAccessRight(AccessRights.ADMIN_PAGES);

  const accountPlanID = document?.accountPlanID;
  const planItems = useMemo(() => (accountPlanID ? [planCatalogItems[accountPlanID]] : null), [accountPlanID]);

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    editAppContext.store.setState((state: AccountState) => ({ document: { ...state.document!, name: event.target.value } }));
  };

  if (isAnonymousTrial) return <AnonymousTrialOnboarding />;

  return (
    <EditAppContext.Provider value={editAppContext}>
      <Section
        title="Account"
        subtitle={
          <span>
            Basic information about this account.
            {isUserAdmin && (
              <Link to={`/admin/account/${document?.id}`} css={adminLinkStyle}>
                Admin
              </Link>
            )}
          </span>
        }
      >
        <div css={profileStyle}>
          <ProfilePicture accountID={document?.id} />
          <Stack>
            <Input label="Name" value={document?.name || ''} large disabled={!isAdmin} onChange={handleNameChange} />
            <div css={identifierStyle}>
              <Tag>{document?.identifier}</Tag>
              <Typography variant="body2">will be used to identify this account</Typography>
            </div>
          </Stack>
        </div>

        {!isAdmin && <Banner>Some account settings are only visible to administrators</Banner>}
      </Section>

      {planItems && document?.accounting && (
        <Section title="Plan" subtitle="View & manage the current plan for this account. Plans are account-specific.">
          <Catalog items={planItems} css={[accountPlanID === AccountPlan.PAID && paidCatalogStyle]} style={{ marginBottom: 16 }} />
          {isUserAdmin && <PaidPlanBreakdown accounting={document.accounting as AccountAccountingSummary} />}
        </Section>
      )}

      {isAdmin && (
        <Section title="Members" subtitle="Manage account members & their access levels.">
          <UserList />
        </Section>
      )}

      <Section title="Actions" subtitle="Run one-time actions for this account." css={[!isAdmin && actionsStyle]}>
        <AccountActions />
      </Section>

      {isAdmin && (
        <Section title="" css={actionsStyle}>
          <EditAppSaveButton icon={<DownloadSimple weight="regular" />} primary large>
            Save Changes
          </EditAppSaveButton>
        </Section>
      )}

      <UpgradeToPaidDialog open={upgradeOpen} accountID={document?.id || 0} onClose={() => setUpgradeOpen(false)} />
    </EditAppContext.Provider>
  );
};

const heartIconURI = (theme: Theme) =>
  `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='192' height='192' ` +
  `fill='%23000000' viewBox='0 0 256 256'%3E%3Crect width='256' height='256' fill='none'%3E%3C/rect%3E%3Cpath ` +
  `d='M133.7,211.9l81-81c19.9-20,22.8-52.7,4-73.6a52,52,0,0,0-75.5-2.1L128,70.5,114.9,57.3c-20-19.9-52.7-22.8-73.6-4a52,52,` +
  `0,0,0-2.1,75.5l83.1,83.1A8.1,8.1,0,0,0,133.7,211.9Z' fill='none' stroke='${theme.color.primary[80].replace('#', '%23')}' ` +
  `stroke-linecap='round' stroke-linejoin='round' stroke-width='16'%3E%3C/path%3E%3C/svg%3E");`;

const identifierStyle = (theme: Theme) => css`
  display: flex;
  margin-top: -0.5rem;
  margin-bottom: 1rem;
  height: 20px;

  > .tag {
    margin-right: 5px;
    background-color: ${theme.color.grey[60]};
    color: ${theme.color.grey[100]} !important;
    user-select: all;
  }

  > p {
    user-select: none;
  }
`;

const actionsStyle = css`
  border-bottom: none;
`;

const paidCatalogStyle = (theme: Theme) => css`
  > li {
    border-color: ${theme.color.primary[100]};
    background-image: ${heartIconURI(theme)};
    background-repeat: no-repeat;
    background-position: 99.5% 10%;
    background-size: 20%;
  }
`;

const profileStyle = css`
  display: flex;
  gap: 12px;

  > div {
    flex: 1;
  }
`;

const adminLinkStyle = css`
  text-decoration: none;
  padding-left: 10px;
  font-size: 80%;
  opacity: 40%;
`;
